import React, { createContext, useContext, useMemo } from 'react';
import { ILogger } from '@honeybook/core';

// Context to hold the logger instance
interface LoggerContextValue {
    logger: ILogger;
}

// Provider props interface
export interface LoggerProviderProps {
    logger: ILogger;
    children: React.ReactNode;
}

// Create the context with initial value
const LoggerContext = createContext<LoggerContextValue | null>(null);

/**
 * Provider component for logger functionality.
 * Makes it available to all child components.
 */
export const LoggerProvider: React.FC<LoggerProviderProps> = ({
    logger,
    children
}) => {
    const contextValue = useMemo(
        () => ({
            logger
        }),
        [logger]
    );

    return (
        <LoggerContext.Provider value={contextValue}>
            {children}
        </LoggerContext.Provider>
    );
};

/**
 * Hook to access the logger from the context.
 * @returns The logger instance
 * @throws Error if used outside of a LoggerProvider
 */
export const useLogger = (): ILogger => {
    const context = useContext(LoggerContext);

    if (!context) {
        throw new Error('useLogger must be used within a LoggerProvider');
    }

    return context.logger;
};
